/**
 * @license
 * Copyright TWISS.IO
 * All Rights Reserved.
 * Licensed under the MIT License.
 */

import { Component, Inject } from "@angular/core";
import { FormBuilder, Validators, AbstractControl } from "@angular/forms";
import { Observable } from "rxjs";

import { map, startWith } from "rxjs/operators";
import { ApiService } from "@app/@core/services";
import { Associate } from "@app/models";
import { MatDialogRef } from "@angular/material/dialog";
import { MAT_DIALOG_DATA } from "@angular/material";
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from "@angular/material-moment-adapter";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";

// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from "moment";
// tslint:disable-next-line:no-duplicate-imports
import { default as _rollupMoment } from "moment";

const moment = _rollupMoment || _moment;

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const salesforce_offboarding_date_formats = {
  parse: {
    dateInput: "LL",
  },
  display: {
    dateInput: "LL",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
  },
};

@Component({
  selector: "admin-salesforce-offboard-dialog",
  templateUrl: "./salesforce-offboard-dialog.component.html",
  styleUrls: ["./salesforce-offboard-dialog.component.scss"],
  providers: [
    // { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_LOCALE, useValue: "en-GB" },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: salesforce_offboarding_date_formats },
  ],
})
export class AdminSalesforceOffboardDialogComponent {
  processing;

  rnf_status;
  onboarding_status;

  authForm = this.formBuilder.group({
    date_last_day: [new Date(), Validators.required],
    date_resigned: [null],
    date_rnf_cessation: [null],
    rnf_status_slug: [""],
    onboarding_status_slug: [""],
  });

  get date_last_day() {
    return this.authForm.controls.date_last_day;
  }
  get date_resigned() {
    return this.authForm.controls.date_resigned;
  }
  get date_rnf_cessation() {
    return this.authForm.controls.date_rnf_cessation;
  }
  get rnf_status_slug() {
    return this.authForm.controls.rnf_status_slug;
  }
  get onboarding_status_slug() {
    return this.authForm.controls.onboarding_status_slug;
  }

  constructor(@Inject(MAT_DIALOG_DATA) private injected_data: any, private formBuilder: FormBuilder, private apiService: ApiService, public dialogRef: MatDialogRef<AdminSalesforceOffboardDialogComponent>) {
    this.rnf_status = injected_data["rnf_status"];
    this.onboarding_status = injected_data["onboarding_status"];
  }

  resetForm(ref = null) {
    if (!ref) {
      this.authForm.reset();
    } else {
      const form_field = this.authForm.get(ref);
      if (!form_field.validator) return form_field.reset();

      const validator = form_field.validator({} as AbstractControl);
      if (validator && validator.required) form_field.setValue("");
      else form_field.reset();
    }
  }

  submitForm() {
    this.processing = true;
    console.log("Offboarding Associate", this.authForm.value);
    let authData = this.authForm.value;
    if (authData.date_last_day) authData.date_last_day = moment(authData.date_last_day).format("YYYY-MM-DD");
    if (authData.date_resigned) authData.date_resigned = moment(authData.date_resigned).format("YYYY-MM-DD");
    if (authData.date_rnf_cessation) authData.date_rnf_cessation = moment(authData.date_rnf_cessation).format("YYYY-MM-DD");
    let url = "admin/associates/" + this.injected_data["associate_uuid"] + "/off-board";
    this.apiService.post(url, authData).subscribe((res) => {
      console.log("Offboarded Associate", res);
      if (!res.error) this.dialogRef.close(res);
      this.processing = false;
    });
  }
}
